import React, { useState, useEffect } from "react";
import axios from "axios";
import { ShoppingCart, GraduationCap, Baby, School, Dumbbell, Stethoscope, Pill, Trees, ExternalLink, ImageOff } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT } from "../data/apartments";
import { toAbsUrl } from "../data/ApartmentDataContext";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Scalable list — add more surroundings articles (schools, shops, transport...) here later.
const ARTICLES = [
  { source: "15min.lt", title: "Pašešupio parkas Marijampolėje", url: "https://share.google/Vyo3T4p7N2oTt0BPG" },
  { source: "Marijampolės savivaldybė", title: "Parko atnaujinimas ir infrastruktūra", url: "https://share.google/A9Fyl0f3Xgcj9QD45" },
  { source: "Turizmo informacijos centras", title: "Lankytinos vietos ir maršrutai", url: "https://share.google/wG86r0t6lEhwjA3o7" },
  { source: "Wikipedia", title: "Šešupė ir Pašešupio parkas", url: "https://share.google/ltUQflDD7zA3hA7sG" },
];

export default function Location() {
  const { t } = useLang();
  const { mapLat, mapLon } = CONTACT;
  const [parkPhoto, setParkPhoto] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setParkPhoto(data?.parkPhoto || null)).catch(() => setParkPhoto(null));
  }, []);

  const d = 0.006;
  const bbox = `${mapLon - d}%2C${mapLat - d}%2C${mapLon + d}%2C${mapLat + d}`;
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${mapLat}%2C${mapLon}`;

  const amenities = [
    { icon: ShoppingCart, label: t("location.shops"), detail: t("location.shopsDetail"), dist: "~40–530 m" },
    { icon: Pill, label: t("location.pharmacies"), detail: "", dist: "~40 m" },
    { icon: Dumbbell, label: t("location.sports"), detail: "", dist: "~280 m" },
    { icon: Stethoscope, label: t("location.medical"), detail: "", dist: "~320 m" },
    { icon: School, label: t("location.schools"), detail: "", dist: "~410 m" },
    { icon: Baby, label: t("location.kindergartens"), detail: "", dist: "~630 m" },
    { icon: GraduationCap, label: t("location.universities"), detail: "", dist: "~2.6 km" },
  ];

  return (
    <div data-testid="location-page" className="pt-28">
      <Seo
        title="Vieta ir aplinka — Sakurų namai Marijampolėje"
        description="Sakurų namai — Parko g. 1D, Marijampolė, prie Pašešupio parko. Parduotuvės, mokyklos ir gamta žingsnio atstumu. Žemėlapis ir aplinka."
        path="/vieta"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t("location.subtitle")}</p>
        <h1 className="font-serif text-5xl sm:text-6xl font-light text-cream">{t("location.title")}</h1>
        <p className="text-cream/70 font-light leading-relaxed max-w-2xl mt-6">{t("location.intro")}</p>
      </section>

      <section data-testid="location-section" className="max-w-7xl mx-auto px-6 lg:px-8 pb-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 rounded-lg overflow-hidden border border-gold/20 h-[460px]">
          <iframe
            title="Sakurų namai — Parko g. 1D, Marijampolė"
            data-testid="location-map"
            src={mapSrc}
            className="w-full h-full"
            style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
            loading="lazy"
          />
        </div>

        <div className="space-y-3">
          <h2 className="text-xs uppercase tracking-[0.25em] text-gold/70 mb-4">{t("location.amenitiesTitle")}</h2>
          {amenities.map((a, i) => (
            <div key={i} className="flex items-center gap-4 border border-cream/10 rounded-lg px-5 py-4 hover:border-gold/30 transition-colors" data-testid={`amenity-${i}`}>
              <a.icon className="text-gold flex-shrink-0" size={24} strokeWidth={1.3} />
              <div className="flex-1">
                <p className="text-cream text-sm">{a.label}</p>
                {a.detail && <p className="text-cream/40 text-xs">{a.detail}</p>}
              </div>
              <span className="text-gold/80 text-sm">{a.dist}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Location & Surroundings */}
      <section data-testid="surroundings-section" className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="flex items-center gap-3 mb-8">
          <Trees className="text-gold" size={26} strokeWidth={1.3} />
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-cream">{t("location.surroundingsTitle")}</h2>
        </div>

        {/* Park card */}
        <div data-testid="park-card" className="border border-gold/20 rounded-lg overflow-hidden bg-charcoal-light">
          <div className="w-full aspect-[16/7] bg-charcoal flex items-center justify-center overflow-hidden">
            {parkPhoto ? (
              <img src={toAbsUrl(parkPhoto)} alt="Pašešupio parkas Marijampolėje prie Sakurų namų" data-testid="park-photo" className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="flex flex-col items-center gap-3 text-cream/30" data-testid="park-photo-placeholder">
                <ImageOff size={40} strokeWidth={1.2} />
                <span className="text-xs uppercase tracking-widest">{t("common.imageComingSoon")}</span>
              </div>
            )}
          </div>
          <div className="p-8">
            <h3 className="font-serif text-2xl text-cream mb-3">{t("location.parkTitle")}</h3>
            <p className="text-cream/70 font-light leading-relaxed max-w-3xl">{t("location.parkText")}</p>
          </div>
        </div>

        {/* Related articles */}
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold/70 mt-12 mb-5">{t("location.articlesTitle")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ARTICLES.map((a, i) => (
            <a
              key={i}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`article-card-${i}`}
              className="group flex flex-col justify-between border border-cream/10 rounded-lg p-6 hover:border-gold/40 hover:bg-cream/5 transition-colors"
            >
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gold/70 mb-2">{a.source}</p>
                <p className="text-cream text-sm leading-snug">{a.title}</p>
              </div>
              <span className="inline-flex items-center gap-1.5 mt-6 text-xs uppercase tracking-widest text-gold group-hover:gap-2.5 transition-all">
                {t("location.readArticle")} <ExternalLink size={13} />
              </span>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
